import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useQueries } from '@tanstack/react-query';
import { CmdLoader } from '@commander-services/gui-components';
import { CmdTable } from '../CmdTable/CmdTable';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import { CUSTOMERS_KEY, REQUIRED } from '../CmdTable/constants';
import {
  POSITION_QUERY_KEY,
  POSITION_TABLE_KEY,
  POSITION_TABLE_SETTINGS,
  POSITION_GPS_TS_KEY,
  TableSettingsKeys,
  POSITION_ADDRESS_KEY,
} from './constants';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import { TPositionRecords, TPositionResponse } from './interfaces';
import useTripPositions from './useTripPositions';
import { customersAtom } from '../../store/recoil/customers';
import { arrayOrder, getTableSettings } from '../CmdTable/CmdTableService';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  tableFiltersAtomFamily,
  totalCountAtom,
} from '../CmdTable/CmdTableState';
import LoaderService from '../../Services/LoaderService';
import ApiService from '../../Services/ApiService';

interface ITripsOverviewDetailProps {
  vehicleId: number;
  id: string;
  vehicleName: string;
  startAddress: string;
  endAddress: string;
}

export default function TripPositions(props: ITripsOverviewDetailProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const { columns, columnOrder, columnVisibility, tableKey } = useTripPositions(props.id);
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(POSITION_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const [filsetFilterForRequest, setFilterForRequest] = useRecoilState<ICmdTableFilter>(
    filterForRequestAtomFamily(tableKey)
  );
  const tableCount = useRecoilValue<number>(totalCountAtom);

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === CUSTOMERS_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumnsTable(orderColumns);
  };

  React.useEffect(() => {
    setFilterForRequest({ vehicleId: props.vehicleId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSettings = async () => {
    const settings = await getTableSettings(tableKey);
    if (settings) {
      handleSetOrderColumns(settings.columns);

      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [`cmdTableSettings-${tableKey}`],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const handleExport = async (rideId: string) => {
    LoaderService.showLoader(true);
    try {
      const payload = {
        offset: 0,
        filter: { vehicleId: props.vehicleId },
        limit: tableCount || 1000000,
        sort: { gpsTs: 'asc' },
      };
      const fileName = `${f({ id: 'tables.tripsOverview.actions.ridePosition.modal' })}.xlsx`;

      const response = await ApiService.postDownload(
        `/api/v2/ride-book/${rideId}/positions/export/xlsx`,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        payload
      );
      if (response) {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        LoaderService.showLoader(false);
      }
    } catch (error) {
      console.error('An error occurred while exporting:', error);
    }
  };

  const { isLoading: isLoadingSettings } = results[0];

  return (
    <div>
      <div className="w-page">
        <div
          className="modal-body"
          style={{
            minHeight: '0',
            // borderBottom: '1px solid #e5e5e5',
            padding: '0 0 30px',
            background: '#fff',
          }}
        >
          <div className="row row--size-sm">
            <div className="col-12 my-1">
              <div className="row row--size-sm">
                <div className="col-12 col-md-6">
                  <div className="form-horizontal">
                    <div className="form-group mb-1">
                      <div className="col-sm-3 control-label text-left font-weight-bold pt-0 pr-md-0">
                        {f({ id: 'tripsOverview.modal.vehicle' })}:
                      </div>
                      <div className="col-sm-9 pl-md-0">{props.vehicleName}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-horizontal">
                <div className="form-group mt-1 mt-md-0 mb-1">
                  <div className="col-sm-3 control-label text-left font-weight-bold pt-0 pr-md-0">
                    {f({ id: 'tripsOverview.modal.rideStart' })}:
                  </div>
                  <div className="col-sm-9 pl-md-0">{props.startAddress}</div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="form-horizontal">
                <div className="form-group mt-2 mt-md-0 mb-1">
                  <div className="col-sm-3 control-label text-left font-weight-bold pt-0 pr-md-0">
                    {f({ id: 'tripsOverview.modal.rideEnd' })}:
                  </div>
                  <div className="col-sm-9 pl-md-0">{props.endAddress}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {filsetFilterForRequest.vehicleId && (
          <div
            className="basic-box__content"
            // style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 140px)' }}
          >
            <div className="row no-gutters mb-2">
              <div className="col col-md" />
              <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
              <div>
                <button
                  className="e-button e-button--square px-2"
                  data-cy="cmd-table-export"
                  type="button"
                  onClick={() => handleExport(props.id)}
                >
                  <i className="cmd-icons cmd-icon-download mr-2" />
                  {f({ id: 'table.export.button' })}
                </button>
              </div>
              <div className="col-auto order-4 order-lg-5">
                <CmdTableSettings
                  tableId={`${POSITION_QUERY_KEY}`}
                  tableName={POSITION_TABLE_SETTINGS}
                  sort={{ column: POSITION_GPS_TS_KEY, direction: 'desc' }}
                  translationKeys={TableSettingsKeys}
                />
              </div>
            </div>
            <div className="d-flex flex-wrap mb-2">
              {tableFiltersKey &&
                tableFiltersKey.map((filterId) => (
                  <FilterChips key={filterId} id={filterId} tableName={tableKey} />
                ))}
            </div>
            {isLoadingSettings && <CmdLoader inContent={true} />}
            <CmdTable<TPositionResponse, TPositionRecords>
              name={tableKey}
              lockedColumns={[POSITION_GPS_TS_KEY, POSITION_ADDRESS_KEY]}
              columns={columns}
              columnOrder={columnOrder}
              columnVisibility={columnVisibility}
            />
          </div>
        )}
      </div>
    </div>
  );
}
